import * as Sentry from '@sentry/react';

/**
 * GradMap — Sentry (frontend / gradmap-frontend).
 * Error monitoring only (no performance tracing).
 * DSN from REACT_APP_SENTRY_DSN; fallback is the public ingest key.
 */

const DSN =
  process.env.REACT_APP_SENTRY_DSN ||
  'https://14592d389899332821fc66db8d25ab2d@o4511457424637952.ingest.us.sentry.io/4511928550424576';

/** Expected user/validation 400s — not product bugs (login, phone taken, counselor miss). */
const EXPECTED_CLIENT_PATHS = [
  /api\/auth\/login/i,
  /api\/contact-details/i,
  /api\/students\/send-verification-code/i,
  /api\/partner\/invite-counselor/i,
];

const GATEWAY_STATUSES = new Set([502, 503, 504]);

export function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

/** Skip HTTP noise: localhost, gateway 502/503/504, expected 400/404 on login & validation routes. */
export function shouldSkipHttpSentryReport(url?: string, status?: number): boolean {
  if (isLocalDevHost()) return true;
  if (status != null && GATEWAY_STATUSES.has(status)) return true;
  if (!url) return false;
  const path = fingerprintApiPath(url);
  if (/api\/auth\/login/i.test(path)) return true;
  if (status === 400 && EXPECTED_CLIENT_PATHS.some((re) => re.test(path))) return true;
  return false;
}

function eventHaystack(event: Sentry.Event, hint?: Sentry.EventHint): string {
  const err: any = hint?.originalException;
  const exceptionValues = event.exception?.values || [];
  const frames = exceptionValues.flatMap((value) => value.stacktrace?.frames || []);
  const frameText = frames
    .map((frame) => `${frame.filename || ''} ${frame.abs_path || ''} ${frame.function || ''}`)
    .join('\n');
  const extra = event.extra ? JSON.stringify(event.extra) : '';
  return [
    err?.message || '',
    event.message || '',
    exceptionValues.map((value) => `${value.type || ''} ${value.value || ''}`).join('\n'),
    frameText,
    extra,
    event.request?.url || '',
    // Runtime field; not on Sentry.Event in @sentry/types 7.x
    (event as Sentry.Event & { culprit?: string }).culprit || '',
  ].join('\n');
}

function shouldDropEvent(event: Sentry.Event, hint?: Sentry.EventHint): boolean {
  if (isLocalDevHost() || event.environment === 'development') return true;

  const haystack = eventHaystack(event, hint);
  const err: any = hint?.originalException;
  const msg = (err && (err.message || String(err))) || event.message || '';

  if (/Network Error|timeout of|canceled|cancelled|ECONNABORTED/i.test(msg)) return true;

  // Userpilot SDK (G, C, Y, M, H, J)
  if (/userpilot|sdk\/version\//i.test(haystack)) return true;
  if (/Cannot redefine property: adoptedStyleSheets/i.test(haystack)) return true;

  // Safari password-manager (X)
  if (/FormMetadata\.js|_logicalFormForControl/i.test(haystack)) return true;

  // Internal courses-audit bookmarklet (V)
  if (/__runCoursesAudit/i.test(haystack)) return true;

  // Gateway blips — keep 500s (real backend), drop 502/503/504
  if (/Request failed with status code 50[234]/i.test(haystack)) return true;
  if (/Failed write:.*\(50[234]\)/.test(haystack)) return true;

  // Axios 400 unhandled-rejection noise; expected writes are also skipped in http.tsx
  if (/Request failed with status code 400/i.test(haystack)) return true;
  if (/Failed write:.*api\/auth\/login/i.test(haystack)) return true;
  if (/Failed write:.*api\/contact-details/i.test(haystack)) return true;
  if (/Failed write:.*send-verification-code/i.test(haystack)) return true;
  if (/Failed write:.*invite-counselor/i.test(haystack)) return true;
  if (/Missing data:.*api\/auth\/login/i.test(haystack)) return true;

  // Expired/invalid access token (401) is handled in http.tsx (clear session + login redirect);
  // callers that don't .catch() would otherwise surface it as an unhandled rejection.
  if (err?.isAuthExpired === true) return true;
  if (/Request failed with status code 401/i.test(haystack)) return true;

  return false;
}

export function initSentry(): void {
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    // REACT_APP_ENV is already staging | production on server .env.
    environment:
      process.env.REACT_APP_SENTRY_ENVIRONMENT ||
      process.env.REACT_APP_ENV ||
      process.env.NODE_ENV ||
      'production',
    tracesSampleRate: 0,
    ignoreErrors: [
      'SESSION_EXPIRED',
      'SESSION_EXISTS',
      'Session revoked',
      'Non-Error promise rejection captured',
      'Request failed with status code 400',
      // Expired session — handled by the http.tsx 401 interceptor.
      'Request failed with status code 401',
      'Request failed with status code 502',
      'Request failed with status code 503',
      'Request failed with status code 504',
    ],
    denyUrls: [/userpilot\.io/i, /sdk\/version\//i, /FormMetadata\.js/i],
    beforeSend(event, hint) {
      if (shouldDropEvent(event, hint)) return null;
      return event;
    },
  });
}

export function reportUnexpected(error: unknown, context?: Record<string, any>): void {
  if (shouldSkipHttpSentryReport(context?.url, context?.status)) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/** Strip ids so 200 students hitting the same missing endpoint = one Sentry issue. */
export function fingerprintApiPath(url?: string): string {
  if (!url) return 'unknown';
  return url
    .split('?')[0]
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
    .replace(/\/\d+/g, '/:id');
}

/**
 * Missing-data / 404 that we caused (lost college link, no row when the page needs one).
 * Warning-level so it is visible and grouped, without looking like a crash.
 */
export function reportMissingData(context: {
  url?: string;
  method?: string;
  status?: number;
  responseMessage?: unknown;
}): void {
  if (shouldSkipHttpSentryReport(context.url, context.status)) return;
  const method = (context.method || 'GET').toUpperCase();
  const path = fingerprintApiPath(context.url);
  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('issue_type', 'missing-data');
    scope.setFingerprint(['missing-data', method, path]);
    scope.setExtra('url', context.url);
    scope.setExtra('method', method);
    scope.setExtra('status', context.status);
    scope.setExtra('responseMessage', context.responseMessage);
    Sentry.captureMessage(`Missing data: ${method} ${path}`);
  });
}

/** Failed save/update (4xx on POST/PUT/PATCH/DELETE). Group by route so silent write bugs surface. */
export function reportFailedWrite(context: {
  url?: string;
  method?: string;
  status?: number;
  responseMessage?: unknown;
}): void {
  if (shouldSkipHttpSentryReport(context.url, context.status)) return;
  const method = (context.method || 'POST').toUpperCase();
  const path = fingerprintApiPath(context.url);
  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('issue_type', 'failed-write');
    scope.setFingerprint(['failed-write', method, path, String(context.status || 'unknown')]);
    scope.setExtra('url', context.url);
    scope.setExtra('method', method);
    scope.setExtra('status', context.status);
    scope.setExtra('responseMessage', context.responseMessage);
    Sentry.captureMessage(`Failed write: ${method} ${path} (${context.status || 'unknown'})`);
  });
}

/** Attach signed-in user id only (no email — FERPA). Call with no args to clear. */
export function setSentryUser(userId?: string | number): void {
  if (!userId) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: String(userId) });
}

export { Sentry };
